import { CRM_FILE_MAX_SIZE_MB } from '../Contracts/CrmContract'
import { CRM_DOCUMENT_FIELD_LABELS as L } from './CrmDocument'
import { ReloadCrmDocumentFilesTask } from './ReloadCrmDocumentFilesTask'

import type { CrmDocumentFileField } from './CrmDocument'
import type { CrmDocumentFilesFormContext } from './ReloadCrmDocumentFilesTask'
import type { IFieldConfig } from '@/Core/Contracts/UI/Form'

interface FileFieldEvent {
    formContext?: CrmDocumentFilesFormContext
}

const savedUuid = (formContext: CrmDocumentFilesFormContext | undefined): string => {
    const uuid = formContext?.values?.uuid

    return typeof uuid === 'string' ? uuid : ''
}

/** Перечитати файли; не вийшло — у полі однаково лишився щойно завантажений файл. */
const reload = (restUrl: string, formContext: CrmDocumentFilesFormContext | undefined): void => {
    void ReloadCrmDocumentFilesTask(restUrl, formContext).catch(() => undefined)
}

/**
 * Опис файлового поля документа: Word чи PDF.
 *
 * Завантаження — одним кроком у вже збережений документ (`SPEC-008` §5.7.1):
 * `POST <restUrl>/{uuid}/word_file` чи `…/pdf_file`, файл одразу стає полем
 * документа. Тому ресурс документа — у `ONE_STEP_UPLOADS` вартового, а тіло
 * збереження форми цих полів не несе.
 *
 * Після завантаження й видалення файли перечитуються: новий Word тягне за
 * собою перегенерований PDF, а видалення Word лишає PDF (`SPEC-008` §5.7.1).
 */
const fileField = (restUrl: string, field: CrmDocumentFileField, suffix: string, accept: string[]): IFieldConfig => ({
    label: L[field],
    // Один файл на поле: новий Word бек заміщує на місці
    isMultiple: false,
    uploadUrl: `${restUrl}/{uuid}/${suffix}`,
    uploadAccept: accept,
    uploadMaxSizeMb: CRM_FILE_MAX_SIZE_MB,
    showViewIcon: false,
    showEditIcon: false,
    // Ядрові ключі, яких немає в `IFieldConfig`: підстановка uuid в адресу й
    // обробники завантаження (видалення ядро шукає в `props`)
    ...({
        uploadUrlReplacer: ({ uploadUrl, formContext }: { uploadUrl: string } & FileFieldEvent) =>
            uploadUrl.replace('{uuid}', encodeURIComponent(savedUuid(formContext))),
        eventHanlers: {
            onAfterUpload: {
                reloadDocumentFiles: ({ formContext }: FileFieldEvent) => reload(restUrl, formContext),
            },
        },
        props: {
            eventHanlers: {
                onAfterDelete: {
                    reloadDocumentFiles: ({ formContext }: FileFieldEvent) => reload(restUrl, formContext),
                },
            },
        },
    } as Record<string, unknown>),
})

/**
 * Поля «Word файл» і «Pdf файл» ресурсу документа клієнта чи перевізника.
 * `restUrl` — колекція документів власника: `/services/client_documents`.
 *
 * ⚠️ `type: 'file'` ресурс пише **сам**, поруч із розгортанням опису: за ним
 * вартовий завантажень (`architectureGuard.test.ts`, `ONE_STEP_UPLOADS`)
 * знаходить файлові поля в коді ресурсу.
 *
 * Межі вибору файлу — білий список беку (`CrmFileMimePolicy`, `SPEC-008`
 * §5.7.2): Word — лише `docx`, PDF — лише `pdf`. PDF вручну — лише поки
 * Word немає: інакше PDF формується з Word, і бек відповідає 422.
 */
export const crmDocumentFileFields = (restUrl: string): Record<CrmDocumentFileField, IFieldConfig> => ({
    wordFile: fileField(restUrl, 'wordFile', 'word_file', ['docx']),
    pdfFile: fileField(restUrl, 'pdfFile', 'pdf_file', ['pdf']),
})
